import {_, React, observer, computed, IDisposer, action, useEffect, classNames, useAsObservableSource, exportAndDownloadService} from '@api'
import {CustomAttributeType} from '@api/graphql'
import {DateRange} from '@blueprintjs/datetime'
import {ChangeLogActionView, ChangeLogContentHeader, LoadingIndicator, useVipLocalStore, VipLocalStore, ChangeLogFilterFieldConfig} from '@modules'
import {ChangeLogTitleBar} from './toolbar'
import {ChangeLogEntriesForCatalogItemAtTimestampStore} from '@modules/changelog/entries'
import {ChangelogFieldType, DEFAULT_CATALOG_USER_SETTINGS, ICatalogFieldConfig, onSiteError, StoresInstance, Workspace} from '@stores'
import * as css from './ChangeLogFlexLayoutTab.css'
import {authStore} from '@utils'
import {IJsonTabNode, TabNode, TabSetNode} from '@flexlayout'
import {CatalogItemChangeEventOperation, FlexLayoutComponentProps, globalStores, IBaseFlexLayoutTabConfig, IFlexLayoutInlineCatalogItemDetailsConfig} from '@stores'
import {moment, useMemo} from '@api'
import {autorun, runInAction, observable, when, toJS, trace} from 'mobx'

export interface IFlexLayoutChangeLogTabConfigSettings {}

export const DEFAULT_CHANGE_LOG_TAB_SETTINGS: IFlexLayoutChangeLogTabConfigSettings = {}

export interface IFlexLayoutChangeLogTabConfig extends IBaseFlexLayoutTabConfig {
  settings: IFlexLayoutChangeLogTabConfigSettings
}

export const NUMBER_OF_CHANGE_LOG_PALETTES = 10

/// region Store

export interface ICatalogItemChangeEvent {
  exId?: string
  _id: string
  _log: {
    changes: Array<{ diff: { before, after }, field: string, id: string, op: CatalogItemChangeEventOperation }>
    index: string
    id: string
    operation: CatalogItemChangeEventOperation
    payload: { id: string, data: { before?, after?, delta? } }

    timestamp: number
    userId?: string
    source?: 'VIP' | 'C8' | string
    isNative?: boolean
    delta?: any
  }
}

export interface ISortWithOrder {
  type: string
  order: 'asc' | 'desc'
  field: ChangeLogFilterFieldConfig
}

export interface IChangeLogFieldConfig extends ICatalogFieldConfig {
  workspace?: string
  workspaceIndex?: string
  values?: Object
}

// export const logFields = ['operation', 'source', 'index', 'timestamp', 'userId']
export const logFields = ['operation', 'source', 'index', 'datetime', 'user']

export class ChangeLogFlexLayoutTabStore extends VipLocalStore {
  @observable hasMore: boolean = true

  constructor(stores: StoresInstance, public props: Omit<MyProps, 'className'>) {
    super(stores)
    this.node.extraData.store = this
    this.catalog.componentStores.changeLogTabStores = this

    if (!this.settings) {
      this.catalog.settings.changelog = DEFAULT_CATALOG_USER_SETTINGS.changelog
    }
    this.selected = this.settings.filterSelected
  }

  @computed get _additionalFieldByName() {
    const {config: {additional}} = this
    return _.keyBy(additional, 'field')
  }

  @computed get filterFields(): Array<IChangeLogFieldConfig> {
    const {filter: {filterFields, workspaceConfig}, workspaces, config: {additional}, trackCurrentWorkspace} = this

    return [
      ...additional, ...filterFields.map(f => {
        const ws = _.first(workspaces.byIndex[f.section?.split('.')?.[1]]),
          workspace = ws?.itemName ?? workspaceConfig?.itemName,
          workspaceIndex = ws?.catalog?.index,
          field = {
            ...f,
            workspace,
            workspaceIndex

          }
        if ((trackCurrentWorkspace && (workspace === workspaceConfig?.itemName || !workspace)) || !trackCurrentWorkspace) {
          return field
        }
      }).filter(_.identity)
    ]
  }

  @computed get trackCurrentWorkspace() { return this.settings?.trackCurrentWorkspace === true && this.flexLayout.activeOrLastCatalogRoot != null}

  @action toggleTrackCurrentWorkspace = () => {this.settings.trackCurrentWorkspace = !this.trackCurrentWorkspace}

  @computed get newLook() { return this.settings?.newLook !== false}

  @action toggleNewLook = () => {this.settings.newLook = !this.newLook}

  get settings() { return this.catalog.settings.changelog}

  get node() { return this.props.flexNode as TabNode}

  /**
   * Remove this and the one in inlineDetails (useMemo there like we do here)
   */
  get overriddenStores() { return this.stores }

  get tabConfig() { return this.props.flexNode.config as IFlexLayoutInlineCatalogItemDetailsConfig}

  @computed get trackedWorkspace(): Workspace {
    const {trackCurrentWorkspace, flexLayout: {activeOrLastCatalogRoot: acr}} = this

    return trackCurrentWorkspace ? acr?.catalog?.workspace : null
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = [
      this.node.autorun_updateFlexNodeCustomSettings(),
      autorun(() => {
        const {node, initialLoad, loading} = this
        node.isLoading = initialLoad || loading
      }, {name: `Upload tab loading state for change log`}),
      autorun(() => {
        const {node, trackedWorkspace, strings} = this
        node.name = trackedWorkspace ? `${strings.changeLog} - ${_.capitalize(trackedWorkspace.catalog.catalogItemName)}`
                                     : strings.changeLog
      }, {name: `Update tab name for changelog as active workspace changes`}),
      autorun(() => {
        const {node, data, initialLoad} = this
        node.itemCount = initialLoad ? undefined : data.length
      }, {name: `Update tab count for changelog`}),
      autorun(() => {
        const {flexLayout: {activeOrLastCatalogRoot: acr}, queryVariables, trackCurrentWorkspace} = this

        if (acr && trackCurrentWorkspace) {
          if (acr?.catalog?.initialized) {
            const {catalog: {refetchId}} = acr
          } else {
            return // Don't run a query until the catalog is ready
          }
        }

        // console.debug({queryVariables})
        runInAction(() => {
          this.refetch()
        })
      }, {name: `Refetch change log when query variables change`}),
      // autorun(() => {
      //   const {searchText, sort} = this
      //   // console.debug({searchText, sort})
      //   runInAction(() => {
      //     this.initialLoad = true
      //     this.loadData()
      //   })
      // }, {name: `Refetch change log when search text changes`}),
      autorun(() => {
        const {workspaces: {workspaceByIndex}} = this
        runInAction(() => {
          Object.keys(workspaceByIndex).forEach(key => {
            const workspace = _.first(workspaceByIndex[key])
            workspace.catalog._getDynamicSectionValues()
          })
        })
      }, {name: `Load section values for change log`})

    ]

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  // @computed get data() {
  //   return data
  // }

  @observable initialLoad: boolean = true
  @observable.ref data: Array<ICatalogItemChangeEvent> = []
  @observable loading = true
  @observable attributePalettes: Record<string, number> = {}

  get tab() {
    return this.stores.vipTabs['change-log']?.tab
  }

  @computed get isRefetch() {
    return !_.isEmpty(this.data) && this.initialLoad
  }

  @action refetch = () => {
    // this.attributePalettes = {}
    this.initialLoad = true
    this._loadDataDebounced()
  }

  @action loadPage = async (page: number) => {
    if (page <= 0) return
    const {loading, initialLoad, page: currentPage, hasMore} = this
    console.log(`Load page: ${page}`, {loading, initialLoad, currentPage, hasMore})
    await this._loadDataDebounced()
  }

  @observable rowsPerPage = 5000 // TODO - switch to react-query and add a date range grouping so that we can independently load an accordion's worth of records
  @observable page = 0

  @action onChangeSubSelection = ({type, field, subSelected}: { type: string, subSelected: any, field?: ChangeLogFilterFieldConfig, isDelete?: boolean }) => {
    const {filters} = this

    if (!type) return
    this.filters[type] = {
      ...filters[type],
      subSelected, field
    }
    // this.refetch()
  }

  @action onExcludeFilters = ({type, field, excluded}: { type: string, excluded: any, field?: ChangeLogFilterFieldConfig, isDelete?: boolean }) => {
    const {filters} = this

    if (!type) return
    this.filters[type] = {
      ...filters[type],
      excluded, field
    }

    // this.refetch()
  }

  @action onChangeFilters = ({type, field, selected, isDelete}: { type: string, selected: any, field?: ChangeLogFilterFieldConfig, isDelete?: boolean }) => {
    const {filters} = this

    if (!type) return
    if (!isDelete) {
      switch (type) {
        case 'operation':
          if (filters[type]?.selected?.includes(selected)) {
            this.filters[type].selected = filters[type].selected.filter(f => f !== selected)
            switch (selected) {
              case 'Changed': {
                this.filters[type].selected = filters[type].selected.filter(f => f !== 'Inner Change')
                break
              }
              case 'Added': {
                this.filters[type].selected = filters[type].selected.filter(f => f !== 'Created')
                break
              }
              case 'Deleted': {
                this.filters[type].selected = filters[type].selected.filter(f => f !== 'Removed')
                break
              }
            }
          } else {
            switch (selected) {
              case 'Changed': {
                this.filters[type].selected.push(...[selected, 'Inner Change'])
                break
              }
              case 'Added': {
                this.filters[type].selected.push(...[selected, 'Created'])
                break
              }
              case 'Deleted': {
                this.filters[type].selected.push(...[selected, 'Removed'])
                break
              }
            }
          }
          break
        default:
          this.filters[type] = {
            ...filters[type],
            selected, field
          }
      }
    } else {
      // this.filters[type] = {selected: [], field}
    }
    // this.refetch()
  }

  @computed get filteredFields() {
    return new Set<string>(Object.keys(this.settings.filters))
  }

  get shouldApplyFilters() { return this.settings.shouldApplyFilters === true && this.filteredFields.size > 0 }

  @action toggleApplyFilters = () => { this.settings.shouldApplyFilters = !this.shouldApplyFilters}

  @action onToggleFilterField = (field: string) => (e?: React.MouseEvent) => {
    const {addFilteredField, removeFilteredField, filteredFields} = this
    filteredFields.has(field) ? removeFilteredField(field) : addFilteredField(field)
  }

  removeFilteredField = (field: string) => {
    delete this.settings.filters[field]
  }

  addFilteredField = (field: string) => {
    if (!this.filteredFields.has(field)) {
      if (!this.settings.filters[field]) {
        this.settings.filters[field] = {equals: [], excluded: []}
      }
    }
  }

  onDateFilterSelection = (field: string, value?: DateRange, filterType?: 'equals' | 'excluded') => {
    this.settings.filters[field][filterType] = value
  }

  onFilterValueSelection = (field: string, value?: ChangelogFieldType, filterType?: 'equals' | 'excluded') => {
    const {settings: {filters}} = this

    if (filters[field][filterType]?.some(e => e === value)) {
      this.settings.filters[field][filterType] = filters[field][filterType].filter(e => e !== value)
    } else {
      this.settings.filters[field][filterType] = [...filters[field][filterType], value]
    }
  }

  @observable selected = observable.array<ChangeLogFilterFieldConfig>([])

  @computed get selectedFields() { return new Map<string, ChangeLogFilterFieldConfig>(this.selected.map(f => [f.field, f]))}

  @observable sortWithOrder = observable.array<ISortWithOrder>([
    {
      type: 'datetime',
      field: {type: 'datetime', label: 'Date', values: ['start', 'end'], field: null},
      order: 'asc'
    }
  ])

  @computed get sortKeyByType() {
    const {sortWithOrder} = this
    return _.keyBy(sortWithOrder, 'type')
  }

  @computed get filterKeyByType() {
    const {selected} = this
    return _.keys(_.keyBy(selected, 'type'))
  }

  @action onClickSortMenuItem = ({key, field, selectedField}: { key: string, field: ChangeLogFilterFieldConfig, selectedField: ChangeLogFilterFieldConfig }) => {
    const {sortKeyByType, sortWithOrder} = this,
      sortKeys = _.keys(sortKeyByType)
    if (selectedField) {
      sortWithOrder.splice(_.indexOf(sortKeys, selectedField.type ?? selectedField.field), 1, {type: key, field, order: 'asc'})

    } else {
      if (_.indexOf(sortKeys, key) !== -1) {
        sortWithOrder.splice(_.indexOf(sortKeys, key), 1)
      } else {
        sortWithOrder.push({type: key, field, order: 'asc'})
      }
    }
  }

  @action moveSortItem = ({field, oldField}) => {
    const {sortWithOrder, sortKeyByType} = this,
      oldFieldIndex = _.findIndex(sortWithOrder, {type: oldField}),
      moveFieldIndex = _.findIndex(sortWithOrder, {type: field}),
      fieldToMove = {...sortKeyByType[field]}

    sortWithOrder.splice(moveFieldIndex, 1)
    sortWithOrder.splice(oldFieldIndex, 0, {type: field, field: fieldToMove.field, order: fieldToMove.order})
  }

  @action toggleSort = ({type}: { type: string }) => {
    const {sortWithOrder, sortKeyByType} = this,
      replaceIndex = _.findIndex(sortWithOrder, {type}),
      sortField = sortKeyByType[type]

    sortWithOrder.splice(replaceIndex, 1, {type, field: sortField.field, order: sortField.order == 'asc' ? 'desc' : 'asc'})
  }

  @action clearSort = () => {
    this.sortWithOrder.clear()
  }

  @action clearFilters = () => {
    this.selected.clear()
    this.settings.filters = {}
    this.settings.shouldApplyFilters = false
  }

  @action onClickClearAllFilters = () => {
    this.selected.clear()
    this.filters = {}
  }

  @computed get queryVariables() {
    const {catalog, settings: {filters}, searchQuery, trackCurrentWorkspace, _additionalFieldByName} = this
    let filterValuesForQuery: Array<any> = [
        {bool: {must_not: {terms: {'_log.operation': ['WORKSPACE_SYNC', 'COLLECTION_VERSION_SYNC']}}, must: [{term: {'_log.isNative': {value: 'true'}}}, {nested: {path: '_log.changes', query: {exists: {field: '_log.changes'}}}}]}}
      ],
      fieldsQuery = {bool: {minimum_should_match: 1, should: []}}

    if (filters) {
      Object.keys(filters).forEach(field => {
        const filter = filters[field],
          def = catalog._fieldByName[field] ?? _additionalFieldByName[field],
          type = def?.def?.type ?? 'String',
          {
            equals: selected, excluded
          } = filter

        if (type === 'DateTime') {
          const dateRange = selected as DateRange,
            start = dateRange[0] !== null ? moment(dateRange[0]) : moment(),
            end = dateRange[1] !== null ? moment(dateRange[1]) : moment()

          filterValuesForQuery.push({
            range: {
              '_log.timestamp': {
                gte: Date.parse(start.set({hour: 0, minute: 0, second: 0}).toDate().toString()),
                lte: Date.parse(end.set({hour: 23, minute: 59, second: 0}).toDate().toString())
              }
            }
          })
          return
        }

        if (!(selected.length || excluded.length)) {
          fieldsQuery.bool.should.push({
            nested: {
              path: '_log.changes',
              query: {
                term: {
                  '_log.changes.field': field
                }
              },
              inner_hits: {
                name: field
              }
            }
          })
        }

        const f = catalog.esFieldFor(def, true, false)
        if (selected.length) {
          fieldsQuery.bool.should.push({
            bool: {
              minimum_should_match: 2,
              should: [
                {
                  terms: {
                    [f]: selected
                  }
                }, {
                  nested: {
                    path: '_log.changes',
                    query: {
                      bool: {
                        minimum_should_match: 2,
                        should: [
                          {
                            term: {
                              '_log.changes.field': field
                            }
                          },
                          ...selected.map(sel => {
                            return {
                              query_string: {
                                fields: ['_log.changes.diff.before', '_log.changes.diff.after'],
                                fuzziness: 'AUTO',
                                query: `*${sel}*`
                              }
                            }
                          }),
                          {
                            terms: {
                              '_log.changes.diff.before': selected
                            }
                          }, {
                            terms: {
                              '_log.changes.diff.after': selected
                            }
                          }
                        ].filter(_.identity)
                      }
                    },
                    inner_hits: {
                      name: field
                    }
                  }
                }
              ].filter(_.identity)
            }
          })
        }

        if (excluded.length) {
          fieldsQuery.bool.should.push({
            nested: {
              path: '_log.changes',
              query: {
                bool: {
                  must: [
                    {
                      term: {
                        '_log.changes.field': field
                      }
                    }
                  ],
                  must_not: [
                    {
                      terms: {
                        [f]: excluded
                      }
                    }, ...selected.map(sel => {
                      return {
                        query_string: {
                          fields: ['_log.changes.diff.before', '_log.changes.diff.after'],
                          fuzziness: 'AUTO',
                          query: `*${sel}*`
                        }
                      }
                    }), {
                      terms: {
                        '_log.changes.diff.before': excluded
                      }
                    }, {
                      terms: {
                        '_log.changes.diff.after': excluded
                      }
                    }
                  ]
                }
              },
              inner_hits: {
                name: `${field}_excluded`
              }
            }
          })
        }

      })
    }

    if (!_.isEmpty(searchQuery)) {
      filterValuesForQuery.push(searchQuery)
    }

    if (fieldsQuery.bool.should.length) {
      filterValuesForQuery.push(fieldsQuery)
    }

    let r = {
      bool: {
        filter: filterValuesForQuery.length > 0 ? filterValuesForQuery : []
      }
    }

    if (trackCurrentWorkspace && this.newLook) {
      const {flexLayout: {activeOrLastCatalogRoot: acr}} = this
      if (acr) {
        const {catalog: {queryVariablesEs: qvEs, index}} = acr

        if (qvEs && qvEs.query) {
          const {query: {bool}} = qvEs

          // console.log(`Original: `, toJS({bool, r}))
          r.bool.filter.push({term: {'_log.index': index}})

          r.bool.filter.push({bool})
          // console.log(`After: `, toJS({r}))
        }
      }
    }

    return r
  }

  /*  @computed get queryVariables_old() {
      const {filters, searchQuery, trackCurrentWorkspace} = this
      let filterValuesForQuery: Array<any> = [
          {bool: {must_not: {terms: {'_log.operation': ['WORKSPACE_SYNC', 'COLLECTION_VERSION_SYNC']}}, must: [{term: {'_log.isNative': {value: 'true'}}}, {nested: {path: '_log.changes', query: {exists: {field: '_log.changes'}}}}]}}
        ],
        fieldsQuery = {bool: {minimum_should_match: 1, should: []}}

      Object.keys(filters).forEach(k => {
          const {selected: filter, field, subSelected, excluded} = filters[k],
            key = field?.section ? k.replace(`${field.section}.`, '') : k

          switch (key) {
            case 'datetime':
              if (filter.filter(date => date).length > 0) {
                const end = moment(filter[1]).add(1439, 'm')

                filterValuesForQuery.push({
                  range: {
                    '_log.timestamp': {
                      gte: Date.parse(filter[0]),
                      lte: Date.parse(end.toDate().toString()) ?? Date.now()
                    }
                  }
                })
              }
              break
            case 'operation': {
              if (filter.length) {
                // console.log('operation length', filter.length)
                filterValuesForQuery.push({
                  nested: {
                    path: '_log.changes',
                    query: {
                      bool: {
                        must: [
                          {
                            terms: {
                              '_log.changes.op': filter
                            }
                          }
                        ]
                      }
                    },
                    inner_hits: {
                      name: key
                    }
                  }
                })
              }
            }
              break
            case 'user': {
              if (filter && filter?.value) {
                const value = Array.isArray(filter.value) ? filter.value.map(v => _.get(v, 'id')) : [filter.value]
                if (value) {
                  filterValuesForQuery.push({
                    bool: {
                      minimum_should_match: 1,
                      should: [
                        {
                          terms: {
                            //TODO - PB - I Will move this hardcoded fields to config
                            '_log.userId': value
                          }
                        }
                      ]
                    }
                  })
                }
              }
              break
            }
            case 'workspace': {
              break
            }
            default: {
              const isEnum = field.def?.type === 'Enum', isBoolean = field.def?.type === 'Boolean'
              if (filter && (!_.isEmpty(filter.value) || subSelected?.value?.length)) {
                const subKey: string = isEnum ? 'name' : field.group?.field || (_.isBoolean(field.filter) ? null : field.filter?.subField),
                  value = Array.isArray(filter.value) ? filter.value.map(v => _.get(v, subKey)) : [filter.value]
                if (subKey) {
                  fieldsQuery.bool.should.push({
                    bool: {
                      must: [
                        {
                          nested: {
                            path: '_log.changes',
                            query: {
                              bool: {
                                minimum_should_match: 2,
                                should: [
                                  {
                                    term: {
                                      '_log.changes.field': key
                                    }
                                  },
                                  {
                                    terms: {
                                      '_log.changes.diff.before': value
                                    }
                                  },
                                  {
                                    terms: {
                                      '_log.changes.diff.after': value
                                    }
                                  }
                                ]
                              }
                            },
                            inner_hits: {
                              name: key
                            }
                          }
                        }
                      ]
                    }
                  })
                } else {
                  if (filter.value?.length || subSelected?.value?.length) {
                    const value = Array.isArray(filter.value) ? filter.value.map(val => isBoolean ? val?.id : val?.name ?? val) : [filter.value]
                    fieldsQuery.bool.should.push({
                      bool: {
                        minimum_should_match: subSelected.value?.length && filter.value?.length ? 3 : 2,
                        should: [
                          filter.value?.length > 0 && {
                            terms: {
                              [key]: value
                            }
                          }, {
                            nested: {
                              path: '_log.changes',
                              query: {
                                bool: {
                                  minimum_should_match: 2,
                                  should: [
                                    {
                                      term: {
                                        '_log.changes.field': key
                                      }
                                    },
                                    isBoolean && {
                                      terms: {
                                        '_log.changes.diff.before': value
                                      }
                                    }, isBoolean && {
                                      terms: {
                                        '_log.changes.diff.after': value
                                      }
                                    }
                                  ].filter(_.identity)
                                }
                              },
                              inner_hits: {
                                name: key
                              }
                            }
                          }, field.section !== undefined && {
                            terms: {
                              'attributes.VIP_ParentName': subSelected.value.map(s => s.label)
                            }
                          }
                        ].filter(_.identity)
                      }
                    })
                  }
                }
              } else if (excluded && excluded?.value?.length) {
                const key = field.section ? k.replace(`${field.section}.`, '') : k,
                  isEnum = field.def?.type === 'Enum',// isBoolean = field.def?.type === 'Boolean',
                  subKey: string = isEnum ? 'name' : field.group?.field || (_.isBoolean(field.filter) ? null : field.filter?.subField),
                  value = Array.isArray(excluded.value) ? excluded.value.map(v => _.get(v, subKey)) : [excluded.value]

                fieldsQuery.bool.should.push({
                  nested: {
                    path: '_log.changes',
                    query: {
                      bool: {
                        must: [
                          {
                            term: {
                              '_log.changes.field': key
                            }
                          }
                        ],
                        must_not: [
                          {
                            terms: {
                              [isEnum ? key : `${key}.${subKey}`]: value
                            }
                          }, {
                            terms: {
                              '_log.changes.diff.before': value
                            }
                          }, {
                            terms: {
                              '_log.changes.diff.after': value
                            }
                          }
                        ]
                      }
                    },
                    inner_hits: {
                      name: `${key}_excluded`
                    }
                  }
                })
              } else {
                fieldsQuery.bool.should.push({
                  nested: {
                    path: '_log.changes',
                    query: {
                      term: {
                        '_log.changes.field': key
                      }
                    },
                    inner_hits: {
                      name: key
                    }
                  }
                })
              }
            }
          }
        }
      )

      if (!_.isEmpty(searchQuery)) {
        filterValuesForQuery.push(searchQuery)
      }

      if (fieldsQuery.bool.should.length) {
        filterValuesForQuery.push(fieldsQuery)
      }
      let r = {
        bool: {
          filter: filterValuesForQuery.length > 0 ? filterValuesForQuery : []
        }
      }

      if (trackCurrentWorkspace && this.newLook) {
        const {flexLayout: {activeOrLastCatalogRoot: acr}} = this
        if (acr) {
          const {catalog: {queryVariablesEs: qvEs, index}} = acr

          if (qvEs && qvEs.query) {
            const {query: {bool}} = qvEs

            // console.log(`Original: `, toJS({bool, r}))
            r.bool.filter.push({term: {'_log.index': index}})

            r.bool.filter.push({bool})
            // console.log(`After: `, toJS({r}))
          }
        }
      }

      return r
    }*/

  @computed get workspaceSortFunctionScore() {
    const {workspaces: {workspaces}} = this,
      workspacesSortedByTitle = _.sortBy(workspaces, ws => ws.title),
      functions: any = [{script_score: {script: `return ${workspaces.length}`}}]

    workspacesSortedByTitle.map((ws, counter) => functions.push({
      weight: counter + 1,
      filter: {
        match: {
          index: ws?.catalog?.index
        }
      }
    }))

    return {
      boost: 1,
      boost_mode: 'replace',
      score_mode: 'min',
      functions
    }
  }

  @computed.struct get queryBody() {
    const {queryVariables, sortKeyByType, workspaceSortFunctionScore} = this,
      queryVariablesWithSort = {function_score: {query: queryVariables, ...workspaceSortFunctionScore}}

    return {
      size: this.rowsPerPage,
      query: sortKeyByType['workspace'] ? queryVariablesWithSort : queryVariables
      // sort
    }
  }

  @computed get sort() {
    const {sortKeyByType} = this,
      sortKeys = _.keys(sortKeyByType)
    return !_.isEmpty(sortKeys) ? [
      ..._.map(sortKeys, (type) => {
        //const fieldKey = _.indexOf(logFields, type) == -1 ? type : `_log.${type}`
        const fieldKey = _.indexOf(logFields, type) == -1 ? type : `_log.${type === 'datetime' ? 'timestamp' : type === 'user' ? 'userId' : type}`
        return {
          [type === 'workspace' ? '_score' : fieldKey]: {
            order: type !== 'datetime' ? sortKeyByType[type].order : sortKeyByType[type].order === 'asc' ? 'desc' : 'asc',
            unmapped_type: 'date'
          }
        }
      })
    ] : []
  }

  @observable searchText: string = null

  @action onSearch = (event) => {
    this.searchText = event.target.value
  }

  @computed get searchQuery() {
    if (!this.searchText || this.searchText.length < 3) return {}
    const {searchText, config: {fields}} = this

    return {
      bool: {
        minimum_should_match: 1,
        should: [
          {
            nested: {
              path: '_log.changes',
              query: {
                bool: {
                  must: {
                    query_string: {
                      fields,
                      fuzziness: 'AUTO',
                      query: `*${searchText}*`
                    }
                  }
                }
              },
              inner_hits: {
                name: 'search_query_results'
              }
            }
          }, {
            query_string: {
              fields,
              fuzziness: 'AUTO',
              query: `*${searchText}*`
            }
          }
        ]
      }
    }
  }

  @computed get index() {
    const {es: {indexes}, filters} = this
    return filters['workspace']?.selected?.length ? filters['workspace'].selected.map(ws => `${ws.catalog.index}-history`).join(',') : indexes.changeLogIndexes
  }

  @observable filters: { [key: string]: { field: ChangeLogFilterFieldConfig, selected: any, subSelected?: any, excluded?: any } } = {}

  _loadDataDebounced = _.debounce(() => this._loadData(), 50)

  _reloadWhen: IDisposer

  @action private _loadData = async () => {
    if (!authStore.userIsAuthenticated) return

    const {es, queryBody, sort, stores: {_id}, initialLoad, index, data, isRefetch} = this

    if (this.loading) {
      if (this._reloadWhen) return

      this._reloadWhen = when(() => !this.loading, () => {
        this._reloadWhen = null
        this._loadData()
      })
    }

    this.loading = true

    try {
      const {items, total: {value: total}} = es.unwrapHitsResult<ICatalogItemChangeEvent>(
        await es.search({
          meta: `Change Log - ${_id}`,
          index,
          body: {
            ...queryBody,
            sort,
            from: initialLoad ? 0 : data.length
          }
        }, {log: {query: false}}))

      runInAction(() => {
        let animateIn = isRefetch,
          firstExistingId = _.first(this.data)?._id

        this.data = _.uniqBy([
          ...(initialLoad ? [] : this.data),
          ...items.map((item) => {
            if (animateIn && firstExistingId === item._id) {
              animateIn = false
            }
            return {...item, animateIn}
          })
        ], d => d._id)
        this.hasMore = data.length < total
      })
    } finally {
      this.loading = false
      this.initialLoad = false
    }
  }
  private _today = ['moments', 'seconds', 'second', 'minutes', 'minute', 'hours', 'hour']

  @computed get _groupedChangeLog(): Record<string, Array<ICatalogItemChangeEvent>> {
    const {data, _today, sortWithOrder} = this
    return _.groupBy(data, d => {
      const firstSort = sortWithOrder?.[0],
        firstSortType = firstSort?.field?.def?.type ?? firstSort?.type,
        firstSortField = firstSort?.type,
        m = firstSortType === 'datetime' ? moment(d._log.timestamp).fromNow(false) : _.get(d, firstSortField)
      if (_.isNil(m)) {
        return 'Unchanged'
      } else if (firstSortType === 'datetime' && _today.some(t => m.includes(t))) {
        return 'Today'
      }
      return m
    })
  }

  @computed get isGridView() {
    return this.settings.isTableView === true
  }

  @action toggleGridView = () => {
    this.settings.isTableView = !this.isGridView
  }

  @observable _selected: Array<string> = []

  @action onClickClearSelected = () => {
    this._selected = []
  }

  @action onSelectChangeLog = (event: React.ChangeEvent<HTMLInputElement>, exId) => {
    const {_selected} = this
    if (event.target.checked) {
      this._selected.push(exId)
    } else {
      this._selected = _selected.filter(sel => sel !== exId)
    }
  }

  @action onClickItemOpenInDetailsPane_old = (item, workspace) => {
    const {flexLayout, catalog, stores: {flexLayoutCatalogRoot}} = this,
      path = catalog.pathForData(null, item, workspace?.config)

    flexLayout.activeTab.openCatalogItemDetailsInNewTab({path, workspace: workspace._id, name: ''})
  }

  @computed get keys() {
    const {_groupedChangeLog} = this

    return Object.keys(_groupedChangeLog)
  }

  @computed get changes() {
    return _.flatMap(this.data, '_log.changes').map(c => c.id)
  }

  @computed get config() {
    const {workspaces: {changeLogConfig}} = this
    return changeLogConfig
  }

  @action exportExcel = async () => {
    const {queryBody, sort, index} = this
    delete queryBody.size
    try {
      const {catalog: {stores: {config: {config: {graphqlEndpoint}}}}, workspaces: {deploymentConfig: {title, changeLog: {exportFields}}}} = this,
        exportBaseUrl: string = `${graphqlEndpoint}excel/export/changelog`
      const name = `${title}-${moment(new Date()).format('MMDDYYYYHHmm')}`
      const exportInput = {
        index,
        query: {
          sort,
          ...queryBody
        },
        name,
        fields: exportFields || [],
        utcOffset: moment().utcOffset()
      }
      await exportAndDownloadService(exportBaseUrl, 'POST', {workbookInputs: [{...exportInput}]}, name)
    } catch (e) {
      onSiteError(`Error while export change log ${e?.message}`)
    }
    // console.log(queryBody, sort, index)
  }

  @observable.ref popoverItem: {
    workspace: Workspace,
    data: any,
    changeEvent: ChangeLogEntriesForCatalogItemAtTimestampStore
  }
}

export const ChangeLogFlexLayoutTabStoreContext = React.createContext<ChangeLogFlexLayoutTabStore>(null)
export const useChangeLogFlexLayoutTabStore = () => React.useContext(ChangeLogFlexLayoutTabStoreContext)

/// endregion

/// region Components

interface MyProps extends FlexLayoutComponentProps {
}

export const ChangeLogFlexLayoutTab = observer<MyProps>(function ChangeLogFlexLayoutTab({className, flexNode}) {
  const obsProps = useAsObservableSource({flexNode}),
    overrideStores = useMemo(() => new StoresInstance(
        `Change Log Tab - ${flexNode.id}`,
        null,
        flexNode.id,
        'change-log',
        {tab: flexNode})
      , []),
    store = useVipLocalStore((ignoredStores) => new ChangeLogFlexLayoutTabStore(overrideStores, obsProps)),
    {loading, _selected, data, settings: {newLook}, isGridView} = store

  useEffect(() => store.registerEffects(), [])

  return (
    <ChangeLogFlexLayoutTabStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)}
           data-new-look={newLook === true ? true : undefined}
           data-type='ChangeLog'
           data-grid-view={isGridView}
      >
        <ChangeLogTitleBar/>
        {(_.isEmpty(data) && loading) ? <LoadingIndicator text={'Loading Change Log...'}/>
                                      : <>{_selected.length > 0 && <ChangeLogContentHeader/>}
            <ChangeLogActionView/></>}
      </div>
    </ChangeLogFlexLayoutTabStoreContext.Provider>)
})

/// endregion

export function createChangeLogTab({tab, tabSet}: { tab?: TabNode, tabSet?: TabSetNode } = {}) {
  const {flexLayout: {create: {createOrUpdateTabView}}} = globalStores

  const newTab: IJsonTabNode = {
    type: 'tab',
    component: 'change-log',
    name: 'Change Log',
    config: {
      settings: _.cloneDeep(DEFAULT_CHANGE_LOG_TAB_SETTINGS)
    } as IFlexLayoutChangeLogTabConfig
  }

  if (tab?.catalogRoot) {
    return tab.catalogRoot.addNewChildTab(newTab)
  } else {
    return createOrUpdateTabView({tabSet, tabJson: newTab, action: 'new'})
  }
}



// WEBPACK FOOTER //
// ./src/modules/changelog/ChangeLogFlexLayoutTab.tsx